// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * modes.ts — the three shapes a round can take (principle #14).
 *
 * A mode must change how the game PLAYS, not a number on a HUD. These three
 * change the geometry of the problem itself:
 *
 *  - Skirmish  9x13, one gate. The baseline: one road to fold.
 *  - Tangle    7x15, one gate. Narrow and long — 105 cells against Skirmish's
 *              117, but a taller field, so the road is naturally long and every
 *              tower spends corridor you cannot easily get back. Waves land
 *              faster, so there is no time to admire it.
 *  - Siege     11x13, TWO gates feeding one Heart. You cannot cover both roads
 *              with one budget, so every wave is a choice about which flank to
 *              under-defend — and a spore that arrives at the weak gate is the
 *              most expensive thing in the game.
 *
 * The host's pick travels FROZEN inside the round start (rematch's roundOpts),
 * because Siege and Tangle have different field dimensions: two peers each
 * reading their own local picker would be playing different games on the same
 * seed. Guests render `state().hostOpts` and never their own selection.
 */

export interface Mode {
  id: string;
  name: string;
  /** One line, shown under the name on the mode picker. */
  blurb: string;
  cols: number;
  rows: number;
  /** Gate columns, on row 0. One entry per gate. */
  gateCols: number[];
  /** Heart column, on the last row. */
  heartCol: number;
  /** Beats the Heart can lose before it stops. */
  heartBeats: number;
  /** Milliseconds between wave starts. */
  waveMs: number;
  /** Gold at the start of a match. */
  startGold: number;
  /**
   * Hard cap on waves. A match that reaches it is decided on Heart beats, then
   * kills. It exists so `balance.test.ts` can assert every match TERMINATES —
   * an endless game is a balance failure that looks like a hang.
   */
  waveCap: number;
}

export const MODES: readonly Mode[] = [
  {
    id: 'skirmish',
    name: 'Skirmish',
    blurb: '9x13 · one gate · the baseline',
    cols: 9,
    rows: 13,
    gateCols: [4],
    heartCol: 4,
    heartBeats: 20,
    waveMs: 22_000,
    startGold: 70,
    waveCap: 40,
  },
  {
    id: 'tangle',
    name: 'Tangle',
    blurb: '7x15 · narrow, long and fast',
    cols: 7,
    rows: 15,
    gateCols: [3],
    heartCol: 3,
    heartBeats: 16,
    waveMs: 17_000,
    startGold: 62,
    waveCap: 40,
  },
  {
    id: 'siege',
    name: 'Siege',
    blurb: '11x13 · TWO gates · one budget',
    cols: 11,
    rows: 13,
    gateCols: [2, 8],
    heartCol: 5,
    heartBeats: 24,
    waveMs: 26_000,
    startGold: 84,
    waveCap: 40,
  },
];

export const DEFAULT_MODE = MODES[0];

/**
 * Resolve a mode id that arrived over the wire.
 *
 * `Object.hasOwn`-style lookup by scan rather than a keyed object, so a hostile
 * or stale id — 'constructor', 'toString', undefined — falls back to the default
 * instead of reaching the field generator as a Mode of undefined fields.
 */
export function modeOf(id: unknown): Mode {
  if (typeof id !== 'string') return DEFAULT_MODE;
  return MODES.find((m) => m.id === id) ?? DEFAULT_MODE;
}

/** The settings frozen into a round start. */
export interface RoundOpts {
  mode: string;
}

export function roundOptsOf(raw: unknown): RoundOpts {
  const mode = raw && typeof raw === 'object' ? (raw as { mode?: unknown }).mode : undefined;
  return { mode: modeOf(mode).id };
}
